// OrderChatPanel.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import moment from 'moment-jalaali';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import ReactLoading from 'react-loading';
import api from '../../../../hooks/api';
import {
    HiOutlineClock,
    HiOutlineChatBubbleLeftRight,
    HiOutlineFlag,
    HiOutlineExclamationTriangle,
    HiOutlinePencil,
    HiArrowDown
} from 'react-icons/hi2';
import { BiShieldX } from 'react-icons/bi';
import { IoChatbubblesOutline } from 'react-icons/io5';
import './OrderChatPanel.css';

// ایمپورت از فایل کمکی سفارشات
import { getStatusTitleById, toPersianDigits } from '../utils';

const getMessageConfig = (groupId) => {
    switch (parseInt(groupId, 10)) {
        case 1: return { name: 'فروشنده', badge: '🏪', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', isSelf: false };
        case 2: return { name: 'خریدار (شما)', badge: '👤', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', isSelf: true };
        case 3: return { name: 'پشتیبانی', badge: '🛡️', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', isSelf: false };
        default: return { name: 'پیام', badge: '💬', color: '#4b5563', bg: '#f9fafb', border: '#e5e7eb', isSelf: false };
    }
};

export const OrderChatPanel = ({ orderId, targetStatusId, isCancelled, isDelivered, isFinalized }) => {
    const [reportDetails, setReportDetails] = useState(null);
    const [isFetchingReport, setIsFetchingReport] = useState(false);
    const [reportText, setReportText] = useState('');
    const [isReportSubmitting, setIsReportSubmitting] = useState(false);
    const [reportResponseText, setReportResponseText] = useState('');
    const [isReportResponseSubmitting, setIsReportResponseSubmitting] = useState(false);
    const [isSubmittingReportToggle, setIsSubmittingReportToggle] = useState(false);

    // استیت‌های تاریخچه
    const [historyData, setHistoryData] = useState([]);
    const [isHistoryVisible, setIsHistoryVisible] = useState(false);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);

    // کشیدن لمسی به پایین
    const [pullDistance, setPullDistance] = useState(0);
    const [isPulling, setIsPulling] = useState(false);
    const touchStartY = useRef(0);
    const panelScrollRef = useRef(null);

    const currentStageName = useMemo(() => {
        if (reportDetails?.header?.status?.title) {
            return reportDetails.header.status.title;
        }
        return getStatusTitleById(targetStatusId);
    }, [reportDetails, targetStatusId]);

    const fetchHistoryMessages = async () => {
        setIsHistoryLoading(true);
        try {
            const response = await api.post(`invoice/reporter/history?page=1`, {
                invoice_id: orderId
            });
            let rawHistoryList = [];
            const resData = response?.data;
            if (resData) {
                if (resData.data && Array.isArray(resData.data.data)) {
                    rawHistoryList = resData.data.data;
                } else if (Array.isArray(resData.data)) {
                    rawHistoryList = resData.data;
                } else if (Array.isArray(resData)) {
                    rawHistoryList = resData;
                }
            }
            setHistoryData(rawHistoryList);
        } catch (error) {
            console.error("Error fetching history:", error);
        } finally {
            setIsHistoryLoading(false);
        }
    };

    const fetchCurrentReport = async () => {
        setIsFetchingReport(true);
        try {
            const response = await api.post(`invoice/reporter/show`, {
                invoice_id: orderId,
                status_id: targetStatusId
            });
            setReportDetails(response.data?.data || null);
        } catch (error) {
            console.error(error);
            setReportDetails(null);
        } finally {
            setIsFetchingReport(false);
        }
    };

    useEffect(() => {
        if (isDelivered || isFinalized) {
            setIsHistoryVisible(true);
            fetchHistoryMessages();
        } else {
            fetchCurrentReport();
        }
    }, [orderId, targetStatusId]);

    const handleOpenHistory = async () => {
        if (isHistoryLoading || isHistoryVisible) return;
        setIsHistoryVisible(true);
        if (historyData.length === 0) {
            await fetchHistoryMessages();
        }
    };

    // ثبت اولین پیام گفت‌وگو
    const handleCreateReport = async () => {
        const textToSend = reportText.trim();
        if (!textToSend || isReportSubmitting) return;

        setIsReportSubmitting(true);
        try {
            const res = await api.post(`invoice/reporter/store`, {
                report_text: textToSend,
                invoice_header_id: orderId,
                reporter_group_id: 2
            });

            if (res.data?.success || res.data?.data?.id) {
                const headerData = res.data?.data && typeof res.data.data === 'object' && res.data.data.id
                    ? res.data.data
                    : {
                        id: res.data?.data?.id || Date.now(),
                        report_text: textToSend,
                        reporter_group_id: 2,
                        created_at: new Date().toISOString()
                    };

                // فقط در صورت موفقیت متن پاک می‌شود
                setReportText('');
                setReportDetails({ header: headerData, details: [] });
                toast.success('پیام شما با موفقیت ثبت شد', { position: 'top-center', rtl: true, autoClose: 2500 });
            } else {
                Swal.fire({ icon: 'error', title: 'خطا', text: res.data?.message || "خطایی رخ داده است" });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({ icon: 'error', title: 'خطا', text: "مشکلی در ثبت پیام پیش آمده است." });
        } finally {
            setIsReportSubmitting(false);
        }
    };

    // ارسال پاسخ در گفت‌وگو بدون لودینگ مجدد کل صفحه
    const handleReportResponseSubmit = async () => {
        const textToSend = reportResponseText.trim();
        if (!textToSend || isReportResponseSubmitting) return;
        if (!reportDetails?.header) return;

        setIsReportResponseSubmitting(true);
        try {
            const res = await api.post(`invoice/respondent/store`, {
                response_text: textToSend,
                reporter_id: reportDetails.header.id,
                reporterGroup_id: 2
            });

            if (res.data?.success || res.status === 200) {
                // استخراج داده پیام از پاسخ سرور یا ساخت آبجکت متناظر
                const serverData = res.data?.data;
                const newDetail = (serverData && typeof serverData === 'object') ? {
                    id: serverData.id || Date.now(),
                    response_text: serverData.response_text || textToSend,
                    reporter_group_id: Number(serverData.reporter_group_id || serverData.reporterGroup_id || 2),
                    created_at: serverData.created_at || new Date().toISOString()
                } : {
                    id: Date.now(),
                    response_text: textToSend,
                    reporter_group_id: 2,
                    created_at: new Date().toISOString()
                };

                // اضافه کردن مستقیم پیام به انتهای لیست پیام‌ها بدون فلش زدن صفحه
                setReportDetails(prev => ({
                    ...prev,
                    details: [...(prev?.details || []), newDetail]
                }));

                // پاک کردن متن تنها پس از ثبت موفق
                setReportResponseText('');
                toast.success('پیام شما با موفقیت ارسال شد', { position: 'top-center', rtl: true, autoClose: 2500 });
            } else {
                Swal.fire({ icon: 'error', title: 'خطا', text: res.data?.message || 'خطا در ثبت پیام' });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({ icon: 'error', title: 'خطا', text: "خطا در اتصال به سرور. لطفاً مجدداً تلاش فرمایید." });
        } finally {
            setIsReportResponseSubmitting(false);
        }
    };

    const handleToggleReport = async () => {
        if (!reportDetails?.header) return;
        const isCurrentlyReported = reportDetails.header.isReportedByBuyer;
        const actionText = isCurrentlyReported ? 'لغو گزارش پشتیبانی' : 'اطلاع به پشتیبانی';

        Swal.fire({
            title: `<h3 style="font-size: 1.25rem; font-weight: 800; margin: 0; color: #1e293b;">${actionText}</h3>`,
            html: `<p style="font-size: 0.95rem; color: #475569; margin-top: 12px; line-height: 1.7;">
                    ${isCurrentlyReported
                    ? "آیا از <b>پس گرفتن گزارش تخلف</b> خود برای این سفارش مطمئن هستید؟"
                    : "پس از ثبت، گزارش جهت بررسی به واحد پشتیبانی ارسال خواهد شد. ادامه می‌دهید؟"}
                   </p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: isCurrentlyReported ? '#475569' : '#e11d48',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: `<span style="font-size: 0.9rem; font-weight: 700; padding: 4px 10px;">${isCurrentlyReported ? 'بله، لغو شود' : 'بله، ثبت و اطلاع داده شود'}</span>`,
            cancelButtonText: '<span style="font-size: 0.9rem; font-weight: 700; padding: 4px 10px; color: #ffffff;">انصراف</span>',
            width: '90%',
            maxWidth: '380px',
            padding: '1.5rem',
            customClass: { popup: 'font-sans' },
            didOpen: () => {
                const popup = Swal.getPopup();
                if (popup) popup.style.borderRadius = '24px';
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                setIsSubmittingReportToggle(true);
                try {
                    const response = await api.post('reports/buyer/store', {
                        report_id: reportDetails.header.id,
                        isReported: !isCurrentlyReported
                    });
                    if (response.data.success || response.status === 200) {
                        setReportDetails(prev => ({
                            ...prev,
                            header: { ...prev.header, isReportedByBuyer: !isCurrentlyReported }
                        }));
                        toast.success(
                            isCurrentlyReported ? 'گزارش شما با موفقیت لغو گردید.' : 'گزارش تخلف با موفقیت به پشتیبانی ارسال شد.',
                            { position: "top-center", theme: "colored", rtl: true, autoClose: 3000 }
                        );
                    }
                } catch {
                    toast.error('خطا در برقراری ارتباط با سرور', { position: "top-center", theme: "colored", rtl: true });
                } finally {
                    setIsSubmittingReportToggle(false);
                }
            }
        });
    };

    const handleTouchStart = (e) => {
        if (isHistoryVisible || isHistoryLoading) return;
        const currentScrollTop = panelScrollRef.current ? panelScrollRef.current.scrollTop : 0;
        if (currentScrollTop <= 2) {
            touchStartY.current = e.touches[0].clientY;
            setIsPulling(true);
        }
    };

    const handleTouchMove = (e) => {
        if (!isPulling || isHistoryVisible || isHistoryLoading) return;
        const currentY = e.touches[0].clientY;
        const diff = currentY - touchStartY.current;
        if (diff > 0) setPullDistance(Math.min(diff * 0.45, 60));
    };

    const handleTouchEnd = () => {
        if (!isPulling) return;
        setIsPulling(false);
        if (pullDistance > 35 && !isHistoryVisible && !isHistoryLoading) {
            handleOpenHistory();
        }
        setPullDistance(0);
    };

    const processedHistory = useMemo(() => {
        if (!historyData || historyData.length === 0) return [];
        const filtered = (isDelivered || isFinalized)
            ? historyData
            : historyData.filter(item => Number(item.status_id) !== Number(targetStatusId));

        const groups = [];
        filtered.forEach(item => {
            const statusTitle = item.status?.title || getStatusTitleById(item.status_id);
            let group = groups.find(g => g.status_title === statusTitle);
            if (!group) {
                group = { status_title: statusTitle, items: [] };
                groups.push(group);
            }
            group.items.push(item);
        });
        return groups;
    }, [historyData, targetStatusId, isDelivered, isFinalized]);

    const renderChatBubble = (msg, key) => {
        const config = getMessageConfig(msg.reporter_group_id);
        return (
            <div key={key} className={`chat-message-wrapper ${config.isSelf ? 'self' : 'other'}`}>
                <div className="chat-message-box" style={{ backgroundColor: config.bg, borderColor: config.border }}>
                    <div className="chat-message-header" style={{ borderBottomColor: config.border }}>
                        <span className="sender-name" style={{ color: config.color }}>{config.badge} {config.name}</span>
                        <span className="message-time">
                            {msg.created_at && moment(msg.created_at).isValid()
                                ? toPersianDigits(moment(msg.created_at).format('HH:mm - jYYYY/jMM/jDD'))
                                : ''}
                        </span>
                    </div>
                    <div className="chat-message-body">
                        {toPersianDigits(msg.text || '')}
                    </div>
                </div>
            </div>
        );
    };

    // تعداد پیام‌های ارسالی توسط خریدار
    const buyerMessagesCount = useMemo(() => {
        if (!reportDetails) return 0;
        let count = (reportDetails.header && String(reportDetails.header.reporter_group_id || reportDetails.header.reporterGroup_id) === '2') ? 1 : 0;
        if (Array.isArray(reportDetails.details)) {
            count += reportDetails.details.filter(d => String(d.reporter_group_id || d.reporterGroup_id) === '2').length;
        }
        return count;
    }, [reportDetails]);

    // کل پیام‌های مرحله جاری
    const totalMessagesCount = useMemo(() => {
        if (!reportDetails?.header) return 0;
        return 1 + (Array.isArray(reportDetails.details) ? reportDetails.details.length : 0);
    }, [reportDetails]);

    // آخرین فرستنده پیام
    const lastGroupId = useMemo(() => {
        if (!reportDetails) return null;
        if (reportDetails.details?.length > 0) {
            const lastDetail = reportDetails.details[reportDetails.details.length - 1];
            return String(lastDetail.reporter_group_id || lastDetail.reporterGroup_id);
        }
        return reportDetails.header ? String(reportDetails.header.reporter_group_id || reportDetails.header.reporterGroup_id || 1) : null;
    }, [reportDetails]);

    const isReportedToSupport = Boolean(reportDetails?.header?.isReportedByBuyer || reportDetails?.header?.isReportedBySeller);
    // خریدار تنها زمانی مجاز به ارسال پیام است که سقفش پر نشده باشد و آخرین پیام از جانب خودش نباشد
    const canBuyerSendResponse = Boolean(!isReportedToSupport && buyerMessagesCount < 2 && totalMessagesCount < 4 && lastGroupId && lastGroupId !== '2');

    return (
        <div
            ref={panelScrollRef}
            className={`order-chat-module-root report-panel ${isCancelled ? 'cancelled-panel' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
                maxHeight: (isHistoryVisible || isDelivered || isFinalized) ? '65vh' : 'none',
                overflowY: (isHistoryVisible || isDelivered || isFinalized) ? 'auto' : 'visible'
            }}
        >
            {/* ۱. نوار اطلاع به پشتیبانی */}
            {reportDetails?.header && (
                <div className="chat-sticky-support-header">
                    <div className="support-actions-row">
                        <div className="badges-group">
                            {reportDetails.header.isReportedByBuyer && (
                                <span className="badge-item">
                                    <span>شما</span>
                                    <HiOutlineFlag size={13} />
                                </span>
                            )}
                            {reportDetails.header.isReportedBySeller && (
                                <span className="badge-item">
                                    <span>فروشنده</span>
                                    <HiOutlineFlag size={13} />
                                </span>
                            )}
                        </div>

                        <button 
                            type="button"
                            className={`support-toggle-btn ${reportDetails.header.isReportedByBuyer ? 'active' : ''}`} 
                            onClick={handleToggleReport} 
                            disabled={isSubmittingReportToggle}
                        >
                            {isSubmittingReportToggle ? (
                                <ReactLoading type="spin" color="#e11d48" height={14} width={14} />
                            ) : (
                                reportDetails.header.isReportedByBuyer ? <BiShieldX size={16} /> : <HiOutlineExclamationTriangle size={16} />
                            )}
                            <span>{reportDetails.header.isReportedByBuyer ? 'لغو گزارش پشتیبانی' : 'اطلاع به پشتیبانی'}</span>
                        </button>
                    </div>
                </div>
            )}

            {(isDelivered || isFinalized) ? (
                <div className="chat-history-container delivered-history-mode">
                    {isHistoryLoading ? (
                        <div className="loading-container">
                            <ReactLoading type="spin" color="#64748b" height={24} width={24} />
                            <span className="loading-text">در حال بارگذاری گفتگوها...</span>
                        </div>
                    ) : processedHistory.length === 0 ? (
                        <div className="chat-history-empty">گفتگویی برای این سفارش ثبت نشده است.</div>
                    ) : (
                        processedHistory.map((group, gIdx) => (
                            <div key={gIdx} className="history-status-group">
                                <div className="chat-history-group-divider">
                                    <div className="chat-history-group-line" />
                                    <span className="chat-history-group-badge">مرحله: {group.status_title}</span>
                                </div>
                                {group.items.map((rG, iIdx) => {
                                    const msgs = [];
                                    if (rG.report_text || rG.description) msgs.push({ text: rG.report_text || rG.description, created_at: rG.created_at, reporter_group_id: rG.reporter_group_id || 1 });
                                    if (Array.isArray(rG.respondents)) rG.respondents.forEach(resp => msgs.push({ text: resp.response_text || resp.description, created_at: resp.created_at, reporter_group_id: resp.reporter_group_id }));
                                    return msgs.map((m, mIdx) => renderChatBubble(m, `${gIdx}-${iIdx}-${mIdx}`));
                                })}
                            </div>
                        ))
                    )}
                </div>
            ) : isFetchingReport ? (
                <div className="loading-container">
                    <ReactLoading type="bubbles" color={isCancelled ? "#e11d48" : "#2563eb"} height={44} width={44} />
                    <span className="loading-text">در حال دریافت پیام‌ها...</span>
                </div>
            ) : (
                <>
                    {/* دکمه باز کردن تاریخچه با استایل Pill */}
                    {!isHistoryVisible && (
                        <div className="chat-history-btn-wrapper">
                            <button
                                type="button"
                                className="history-trigger-pill-btn"
                                onClick={handleOpenHistory}
                                disabled={isHistoryLoading}
                            >
                                {isHistoryLoading ? (
                                    <ReactLoading type="spin" color="#64748b" height={13} width={13} />
                                ) : (
                                    <IoChatbubblesOutline size={16} />
                                )}
                                <span>تاریخچه گفتگو</span>
                            </button>
                        </div>
                    )}

                    {pullDistance > 0 && !isHistoryVisible && (
                        <div className={`chat-pull-indicator ${pullDistance > 35 ? 'triggered' : ''}`} style={{ height: `${pullDistance}px`, transition: isPulling ? 'none' : 'height 0.2s ease' }}>
                            <HiArrowDown />
                            <span>{pullDistance > 35 ? 'رها کنید برای بارگذاری تاریخچه' : 'به پایین بکشید...'}</span>
                        </div>
                    )}

                    {/* لیست پیام‌های مراحل گذشته (تاریخچه) */}
                    {isHistoryVisible && (
                        <div className="history-chat-stream">
                            {isHistoryLoading ? (
                                <div className="loading-container">
                                    <ReactLoading type="spin" color="#64748b" height={22} width={22} />
                                    <span className="loading-text">در حال بارگذاری تاریخچه...</span>
                                </div>
                            ) : processedHistory.length === 0 ? (
                                <div className="chat-history-empty">
                                    تاریخچه‌ای برای مراحل قبلی یافت نشد.
                                </div>
                            ) : (
                                processedHistory.map((group, gIdx) => (
                                    <div key={gIdx} className="history-status-group">
                                        <div className="chat-history-group-divider">
                                            <div className="chat-history-group-line" />
                                            <span className="chat-history-group-badge">مرحله: {group.status_title}</span>
                                        </div>
                                        {group.items.map((rG, iIdx) => {
                                            const msgs = [];
                                            if (rG.report_text || rG.description) msgs.push({ text: rG.report_text || rG.description, created_at: rG.created_at, reporter_group_id: rG.reporter_group_id || 1 });
                                            if (Array.isArray(rG.respondents)) rG.respondents.forEach(resp => msgs.push({ text: resp.response_text || resp.description, created_at: resp.created_at, reporter_group_id: resp.reporter_group_id }));
                                            return msgs.map((m, mIdx) => renderChatBubble(m, `hist-${gIdx}-${iIdx}-${mIdx}`));
                                        })}
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {/* خط و برچسب تفکیک‌کننده مرحله جاری */}
                    {isHistoryVisible && (
                        <div className="chat-history-group-divider current-group-divider">
                            <div className="chat-history-group-line" />
                            <span className="chat-history-group-badge current-stage-badge">
                                مرحله: {currentStageName} (جاری)
                            </span>
                        </div>
                    )}

                    {/* فرم ارسال پیام جدید یا نمایش گفتگوی مرحله جاری */}
                    {!reportDetails || !reportDetails.header ? (
                        <div className="chat-form-group">
                            <div className="chat-new-header">
                                <HiOutlineChatBubbleLeftRight size={18} />
                                <span>گفتگو با فروشنده (ارسال پیام جدید)</span>
                            </div>
                            <textarea 
                                className="chat-input" 
                                placeholder="پیام یا توضیحات خود را بنویسید..." 
                                value={reportText} 
                                onChange={(e) => setReportText(e.target.value)} 
                                disabled={isReportSubmitting}
                                readOnly={isReportSubmitting}
                                style={{
                                    opacity: isReportSubmitting ? 0.7 : 1,
                                    cursor: isReportSubmitting ? 'not-allowed' : 'text'
                                }}
                            />
                            <button 
                                className="chat-submit-btn" 
                                onClick={handleCreateReport} 
                                disabled={isReportSubmitting || !reportText.trim()}
                            >
                                {isReportSubmitting && <ReactLoading type="spin" color="#ffffff" height={14} width={14} />}
                                <span>ثبت و ارسال پیام</span>
                            </button>
                        </div>
                    ) : (
                        <div className="chat-history-container">
                            {/* پیام‌های مرحله جاری */}
                            {(() => {
                                const msgs = [];
                                if (reportDetails.header) {
                                    msgs.push({
                                        text: reportDetails.header.report_text || reportDetails.header.description || '',
                                        created_at: reportDetails.header.created_at,
                                        reporter_group_id: reportDetails.header.reporter_group_id || 1
                                    });
                                }
                                if (Array.isArray(reportDetails.details)) {
                                    reportDetails.details.forEach(d => {
                                        msgs.push({
                                            text: d.response_text || d.description || '',
                                            created_at: d.created_at,
                                            reporter_group_id: d.reporter_group_id
                                        });
                                    });
                                }
                                return msgs.map((m, idx) => renderChatBubble(m, `cur-${idx}`));
                            })()}

                            {isReportedToSupport ? (
                                <div className="chat-limit-warning support-locked">
                                    این سفارش به پشتیبانی گزارش شده است. تا زمان بررسی پشتیبانی امکان ثبت پیام جدید وجود ندارد. 🔒
                                </div>
                            ) : (buyerMessagesCount >= 2 || totalMessagesCount >= 4) ? (
                                <div className="chat-limit-warning">سقف مجاز گفتگو (دو پیام) برای شما به پایان رسیده است. 🔒</div>
                            ) : canBuyerSendResponse ? (
                                <div className="chat-reply-container">
                                    <div className="chat-reply-header"><HiOutlinePencil size={15} /> ارسال پیام جدید:</div>
                                    <textarea 
                                        className="chat-input" 
                                        placeholder="پاسخ خود را بنویسید..." 
                                        value={reportResponseText} 
                                        onChange={(e) => setReportResponseText(e.target.value)} 
                                        disabled={isReportResponseSubmitting}
                                        readOnly={isReportResponseSubmitting}
                                        style={{
                                            opacity: isReportResponseSubmitting ? 0.7 : 1,
                                            cursor: isReportResponseSubmitting ? 'not-allowed' : 'text'
                                        }}
                                    />
                                    <button 
                                        className="chat-submit-btn" 
                                        onClick={handleReportResponseSubmit} 
                                        disabled={isReportResponseSubmitting || !reportResponseText.trim()}
                                    >
                                        {isReportResponseSubmitting && <ReactLoading type="spin" color="#ffffff" height={14} width={14} />}
                                        <span>ارسال پاسخ</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="chat-waiting-msg">
                                    <HiOutlineClock size={16} /> پیام شما ارسال شده است. لطفاً منتظر پاسخ فروشگاه بمانید.
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
